import React from 'react';
import PropTypes from 'prop-types';
import { useTable, usePagination, useFilters, useSortBy, useRowSelect } from 'react-table';
import { Link } from 'react-router-dom';
import IndeterminateCheckbox from 'components/IndeterminateCheckbox';
import FreeTextFilter from './Filters/FreeTextFilter';
import useResetSortBy from './Hooks/useResetSortBy';
import Pager from './Pager';

const ReactTable = ({
  data,
  gridCriteria,
  onGridCriteriaChanged,
  onRowSelectionChanged,
  onRowExpandedStateChanged,
  actions,
  enableExpandCollapse,
  enableSelection,
  childRowPropertyName,
  getChildRowLayout,
  initialLoaded,
  loading,
  className,
  collapseHeight,
  enablePagination
}) => {
  const onSelectCheckboxClicked = (rows) => {
    gridCriteria.selectedItems = rows.map(d => d.original);
    if (typeof onRowSelectionChanged === 'function') {
      onRowSelectionChanged({ selectedItems: gridCriteria.selectedItems });
    }
  };

  const expandCollapse = {
    id: 'expand-collapse-column',
    Header: () => (''),
    // eslint-disable-next-line react/prop-types, react/no-unstable-nested-components
    Cell: ({ row }) => (
      <button
        type="button"
        className={ `btn btn-sm mr-0 ${ row.original.isLoadingDetails ? 'loading' : '' }` }
        title={ `${ childRowPropertyName && row.original[childRowPropertyName] == null ? 'Display' : 'Hide' } additional details` }
        onClick={ () => {
          if (enableExpandCollapse && onRowExpandedStateChanged) {
            onRowExpandedStateChanged(row, childRowPropertyName && row.original[childRowPropertyName] == null);
          }
        } }
      >
        { (childRowPropertyName && row.original[childRowPropertyName] != null) ? '▲' : '▼' }
      </button>
    )
  };

  const defaultColumn = React.useMemo(() => ({ Filter: FreeTextFilter }), []);
  const rowsFound = gridCriteria.itemCount > 0;

  const getPreSelectedRows = () => {
    if (data && data.length > 0 && gridCriteria && gridCriteria.selectedItems.length > 0) {
      const selectedIds = gridCriteria.selectedItems.map(v => v.itemId);

      const selectedData = data.map((v, i) => {
        const item = {
          key: i,
          value: selectedIds.includes(v.itemId)
        };
        return item;
      });

      const result = {};
      for (let i = 0; i < selectedData.length; i += 1) {
        if (selectedData[i].value) {
          result[selectedData[i].key] = selectedData[i].value;
        }
      }

      return result;
    }
    return [];
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    setHiddenColumns,
    state: { sortBy, filters },
    setAllFilters,
    selectedFlatRows,
    resetSortBy,
    toggleAllRowsSelected
  } = useTable(
    {
      data,
      columns: gridCriteria.columnDefs,
      pageCount: gridCriteria.pageCount,
      defaultColumn,
      initialState: {
        pageIndex: gridCriteria.pageIndex,
        filters: gridCriteria.filters ?? [],
        sortBy: gridCriteria.sortBy,
        hiddenColumns: gridCriteria.columnDefs.filter(i => i.isVisible === false).map(i => i.accessor),
        selectedRowIds: getPreSelectedRows()
      },
      manualPagination: true,
      manualFilters: true,
      manualGlobalFilter: true,
      manualSortBy: true,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    useResetSortBy,
    (hooks) => {
      hooks.allColumns.push((cols) => {
        const combinedCols = [];
        if (enableSelection) {
          combinedCols.push({
            id: 'selection-column',
            // eslint-disable-next-line react/no-unstable-nested-components
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox
                { ...getToggleAllRowsSelectedProps() }
              />
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            Cell: ({ row }) => (
              <IndeterminateCheckbox
                { ...row.getToggleRowSelectedProps() }
                className="cell-checkbox"
                data-item-id={ row.original.itemId }
              />
            )
          });
        }
        combinedCols.push(...cols);
        if (actions) {
          combinedCols.push(actions);
        }
        if (enableExpandCollapse && onRowExpandedStateChanged) {
          combinedCols.push(expandCollapse);
        }

        return combinedCols;
      });
    }
  );

  React.useEffect(() => {
    if (initialLoaded) {
      gotoPage(0);
      setAllFilters([]);
      resetSortBy();
      toggleAllRowsSelected(false);
    }
    setHiddenColumns(gridCriteria.columnDefs.filter(i => i.isVisible === false).map(i => i.accessor));
  }, [gridCriteria.columnDefs]);

  React.useEffect(() => {
    gotoPage(gridCriteria.pageIndex);
  }, [gridCriteria.pageIndex]);

  React.useEffect(() => {
    if (initialLoaded) {
      gotoPage(0);
      toggleAllRowsSelected(false);
    }
  }, [gridCriteria.itemCount]);

  React.useEffect(() => {
    onSelectCheckboxClicked(selectedFlatRows);
  }, [selectedFlatRows]);

  React.useEffect(() => {
    const newGridCriteria = { ...gridCriteria, sortBy, filters, selectedItems: [], pageIndex: 0 };
    onGridCriteriaChanged(newGridCriteria);
  }, [gridCriteria.pageSize, sortBy, filters]);

  const getHeaderClassName = (index, col) => {
    if (enableSelection && index <= 0) {
      return 'checkbox-header-cell';
    }
    return col.id === 'expand-collapse-column' ? 'expand-header-cell' : col.className ?? 'col-10p';
  };

  const getCellClassName = (index, col) => {
    if (enableSelection && index <= 0) {
      return 'checkbox-cell';
    }
    return col.id === 'expand-collapse-column' ? 'expand-cell' : col.className ?? 'col-10p';
  };

  const getSortIcon = (column) => {
    if (column.isSorted) {
      if (column.isSortedDesc) {
        return <span className="icon icon-arrow-down" id={ column.sortIconId }>&nbsp;&nbsp;</span>;
      }
      return <span className="icon icon-arrow-up" id={ column.sortIconId }>&nbsp;&nbsp;</span>;
    }
    return <span className="icon icon-resize-vert" id={ column.sortIconId }>&nbsp;&nbsp;</span>;
  };

  return (
    <div className="react-table-container">
      <table { ...getTableProps() } className={ `table table-striped table-vert-scroll react-table table-hover table-bordered ${ className ?? '' } ` }>
        <thead>
          { headerGroups.map((headerGroup, i) => (
            <tr key={ headerGroup.id } { ...headerGroup.getHeaderGroupProps() } className="noselect">
              { headerGroup.headers.map((column, j) => (
                <th { ...column.getHeaderProps() } key={ headerGroup.headers[j].id } id={ headerGroup.headers[j].id } className={ getHeaderClassName(j, headerGroup.headers[j]) }>
                  { column.render('Header') }
                  <span className="column-actions">
                    { column.canSort && ((j > 0 && j < headerGroup.headers.length - 1) || !enableSelection) && <span className="sort-span" { ...column.getHeaderProps(column.getSortByToggleProps()) }>
                      { getSortIcon(column) }
                    </span>}
                    { column.canFilter ? column.render('Filter') : null }
                  </span>
                </th>
              )) }
            </tr>
          )) }
        </thead>
        {!rowsFound && !loading && <tbody className={ collapseHeight ? 'height-auto' : '' }><tr className="no-rows"><td><div>No Rows to Show</div></td></tr></tbody> }
        { loading && <tbody><tr className="loading-row"><td><div className="loading loading-lg" /></td></tr></tbody> }
        {!loading && rowsFound && <tbody className={ collapseHeight ? 'height-auto' : '' } { ...getTableBodyProps() }>
          { page.map((row, i) => {
            prepareRow(row);
            return (
              <React.Fragment key={ row.id }>
                <tr key={ row.id } { ...row.getRowProps() }>
                  {row.cells.map((cell, j) => <td key={ cell.id } className={ getCellClassName(j, cell.column) } { ...cell.getCellProps() }>{cell.column.isLinkToAccount ? (
                    <Link className="text-bold" to={ `/account/${ row.original.accountId }` }>
                      {cell.render('Cell')}
                    </Link>
                  ) : (
                    cell.render('Cell')
                  )}</td>) }
                </tr>
                { row.original[childRowPropertyName] && getChildRowLayout && <tr key={ `${ row.id }-addon` } className="child-row">
                  <td colSpan={ row.cells.length }>
                    <div className="container">
                      { getChildRowLayout(row.original[childRowPropertyName]) }
                    </div>
                  </td>
                </tr> }
              </React.Fragment>
            );
          }) }
        </tbody> }
      </table>
      {enablePagination && initialLoaded && <Pager
        page={ {
          index: gridCriteria.pageIndex,
          size: gridCriteria.pageSize,
          length: page.length,
          canPreviousPage,
          canNextPage
        } }
        pageCount={ pageCount }
        itemCount={ gridCriteria.itemCount }
        totalLimit={ gridCriteria.totalLimit }
        onPageChanged={ (p) => {
          gotoPage(p);
          const newGridCriteria = { ...gridCriteria, pageIndex: p };
          onGridCriteriaChanged(newGridCriteria);
        } }
        onPageSizeChanged={ (pageSize) => {
          gotoPage(0);
          gridCriteria.pageSize = pageSize;
          onGridCriteriaChanged(gridCriteria);
        } }
      /> }
    </div>
  );
};

ReactTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()),
  gridCriteria: PropTypes.shape({
    selectedItems: PropTypes.arrayOf(PropTypes.shape()),
    itemCount: PropTypes.number,
    totalLimit: PropTypes.number,
    pageSize: PropTypes.number,
    pageIndex: PropTypes.number,
    pageCount: PropTypes.number,
    filters: PropTypes.arrayOf(PropTypes.shape()),
    sortBy: PropTypes.arrayOf(PropTypes.shape()),
    columnDefs: PropTypes.arrayOf(PropTypes.shape()),
  }),
  onGridCriteriaChanged: PropTypes.func,
  onRowSelectionChanged: PropTypes.func,
  onRowExpandedStateChanged: PropTypes.func,
  getToggleAllRowsSelectedProps: PropTypes.func,
  actions: PropTypes.shape(),
  row: PropTypes.shape(),
  enableExpandCollapse: PropTypes.bool,
  enableSelection: PropTypes.bool,
  childRowPropertyName: PropTypes.string,
  getChildRowLayout: PropTypes.func,
  initialLoaded: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  collapseHeight: PropTypes.bool,
  enablePagination: PropTypes.bool,
};

ReactTable.defaultProps = {
  data: null,
  gridCriteria: null,
  onGridCriteriaChanged: null,
  onRowSelectionChanged: null,
  onRowExpandedStateChanged: null,
  getToggleAllRowsSelectedProps: null,
  actions: null,
  row: null,
  enableExpandCollapse: true,
  enableSelection: false,
  childRowPropertyName: null,
  getChildRowLayout: null,
  initialLoaded: false,
  loading: true,
  className: null,
  collapseHeight: false,
  enablePagination: true,
};

export default ReactTable;
